import { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, Clock, Search } from 'lucide-react';
import {
  autocompleteOrigin,
  autocompleteDestination,
  buildLabel,
  LocationApiSuggestion,
} from '../services/autocomplete.service';

type AutocompleteMode = 'origin' | 'destination';

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;

  // NUEVO:
  mode?: AutocompleteMode;              // 'origin' por default
  originCity?: string;                  // requerido si mode='destination'
  onSelectSuggestion?: (s: LocationApiSuggestion) => void;

  placeholder?: string;
  className?: string;
  label?: string;
  showSwapButton?: boolean;
  onSwap?: () => void;
}

// Recientes opcionales (pueden quedar mock por ahora)
const RECENT_LOCATIONS: Array<{ id: string; label: string; subtitle: string }> = [
  { id: 'r1', label: 'Central TAPO, Ciudad de México, CDMX', subtitle: 'Reciente' },
  { id: 'r2', label: 'Terminal Guadalajara, Guadalajara, Jalisco', subtitle: 'Reciente' },
];

export const LocationAutocomplete = ({
  value,
  onChange,
  mode = 'origin',
  originCity = '',
  onSelectSuggestion,
  placeholder = 'Origen',
  className = '',
  label,
  showSwapButton = false,
  onSwap,
}: LocationAutocompleteProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<LocationApiSuggestion[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const showRecents = useMemo(() => !value.trim() && mode === 'origin' && RECENT_LOCATIONS.length > 0, [value, mode]);
  const hasResults = suggestions.length > 0;

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    if (isFocused) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFocused]);

  // Fetch suggestions (debounce + abort)
  useEffect(() => {
    if (!isFocused) return;

    const q = value.trim();

    // Para no spamear el backend
    if (q.length < 2) {
      setSuggestions([]);
      setIsLoading(false);
      abortRef.current?.abort();
      return;
    }

    // En destino, si no hay origen seleccionado, no hay nada que buscar
    if (mode === 'destination' && !originCity.trim()) {
      setSuggestions([]);
      setIsLoading(false);
      abortRef.current?.abort();
      return;
    }

    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      setIsLoading(true);
      try {
        const data =
          mode === 'origin'
            ? await autocompleteOrigin(q, ac.signal)
            : await autocompleteDestination(originCity, q, ac.signal);

        // Asegura label para UI
        const normalized = (data || []).map((s) => ({
          ...s,
          label: s.label || buildLabel(s),
        }));

        setSuggestions(normalized);
      } catch (e: any) {
        if (e?.name !== 'AbortError') {
          console.error('Autocomplete error:', e);
        }
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [value, isFocused, mode, originCity]);

  const handleSelect = (s: LocationApiSuggestion) => {
    const label = s.label || buildLabel(s);
    onChange(label);
    onSelectSuggestion?.({ ...s, label });
    setIsFocused(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsFocused(false);
      inputRef.current?.blur();
    }
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;
    const safe = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${safe})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <strong key={index} className="font-semibold text-[#111827]">
          {part}
        </strong>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="hidden lg:block text-sm font-medium text-[#111827] mb-2 text-left">
          {label}
        </label>
      )}

      <div className="relative">
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280] z-10 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full pl-12 pr-4 py-4 lg:py-3.5 bg-[#F7F8FA] lg:bg-white border-none lg:border lg:border-[#E5E7EB] rounded-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent transition-all text-[#111827] font-medium"
          autoComplete="off"
        />

        {showSwapButton && onSwap && (
          <div className="lg:hidden absolute right-4 top-1/2 -translate-y-1/2">
            <button
              type="button"
              onClick={onSwap}
              className="w-10 h-10 bg-[#111827] text-white rounded-full flex items-center justify-center hover:bg-[#FF6A2A] transition-colors"
              aria-label="Intercambiar origen y destino"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {isFocused && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#E5E7EB] rounded-[14px] shadow-lg max-h-[400px] overflow-y-auto z-[60]"
        >
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="inline-block w-8 h-8 border-3 border-[#E5E7EB] border-t-[#FF6A2A] rounded-full animate-spin"></div>
              <p className="text-sm text-[#6B7280] mt-3">Buscando...</p>
            </div>
          ) : (!showRecents && !hasResults) ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 bg-[#F7F8FA] rounded-full flex items-center justify-center mx-auto mb-3">
                <Search className="w-6 h-6 text-[#6B7280]" />
              </div>
              <p className="font-semibold text-[#111827] mb-1">No encontramos coincidencias</p>
              <p className="text-sm text-[#6B7280]">Intenta con otra terminal o ciudad</p>
            </div>
          ) : (
            <>
              {showRecents && (
                <div className="border-b border-[#E5E7EB]">
                  <div className="px-4 py-2">
                    <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">
                      Recientes
                    </p>
                  </div>

                  {RECENT_LOCATIONS.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => onChange(r.label)}
                      className="w-full flex items-start gap-3 px-4 py-3 hover:bg-[#F7F8FA] transition-colors text-left"
                    >
                      <div className="w-10 h-10 bg-[#F7F8FA] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Clock className="w-5 h-5 text-[#6B7280]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#111827] truncate">{r.label}</p>
                        <p className="text-sm text-[#6B7280] truncate">{r.subtitle}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!showRecents && (
                <div className="px-4 py-2">
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">
                    Sugerencias
                  </p>
                </div>
              )}

              {suggestions.map((s, i) => {
                const title = s.label || buildLabel(s);
                const subtitle = `${s.city || ''}${s.state ? `, ${s.state}` : ''}`.trim();
                return (
                  <button
                    key={(s.centralId || s.routeId || title || i) as any}
                    onClick={() => handleSelect(s)}
                    className="w-full flex items-start gap-3 px-4 py-3 hover:bg-[#F7F8FA] transition-colors text-left"
                  >
                    <div className="w-10 h-10 bg-[#F7F8FA] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MapPin className="w-5 h-5 text-[#6B7280]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#111827] truncate">
                        {highlightMatch(title, value)}
                      </p>
                      <p className="text-sm text-[#6B7280] truncate">
                        {highlightMatch(subtitle, value)}
                      </p>
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};
